/**
 * GenerationProgress Component
 * 
 * Displays a vertical stepper showing progress during template generation
 * Shows animated progress as steps complete
 * 
 * Uses Ant Design theme variables for consistent styling
 */

import React from "react";
import { Steps, Typography } from "antd";
import { LoadingOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { GenerationStep } from "../types";
import { SHARED_STYLES } from "../constants";

const { Text } = Typography;

interface GenerationProgressProps {
  currentStep: number;
  steps: GenerationStep[];
  isGenerating: boolean;
}

const GenerationProgress: React.FC<GenerationProgressProps> = ({
  currentStep,
  steps,
  isGenerating,
}) => {
  // Don't render if not generating and no progress
  if (!isGenerating && currentStep < 0) {
    return null;
  }

  const isComplete = currentStep >= steps.length;

  return (
    <div
      style={{
        padding: SHARED_STYLES.paddingMd,
        backgroundColor: isComplete ? SHARED_STYLES.successBg : SHARED_STYLES.primaryBg,
        borderRadius: SHARED_STYLES.borderRadiusLg,
        border: `1px solid ${isComplete ? SHARED_STYLES.successBorder : SHARED_STYLES.primaryColor}`,
        transition: "all 0.3s ease",
      }}
    >
      <Text 
        strong 
        style={{ 
          marginBottom: 16, 
          display: "block",
          color: isComplete ? SHARED_STYLES.successColor : SHARED_STYLES.primaryColor,
        }}
      >
        {isComplete ? "✓ Template Generated!" : "Generating Template..."}
      </Text>
      <Steps
        direction="vertical"
        size="small"
        current={currentStep}
        items={steps.map((step, index) => ({
          title: (
            <Text
              style={{
                color: index < currentStep 
                  ? SHARED_STYLES.successColor 
                  : index === currentStep 
                    ? SHARED_STYLES.textColor 
                    : SHARED_STYLES.textTertiary,
                fontSize: SHARED_STYLES.fontSizeSm,
              }}
            >
              {step.label}
            </Text>
          ),
          icon:
            index < currentStep ? (
              <CheckCircleOutlined style={{ color: SHARED_STYLES.successColor, fontSize: 16 }} />
            ) : index === currentStep && isGenerating ? (
              <LoadingOutlined spin style={{ color: SHARED_STYLES.primaryColor, fontSize: 16 }} />
            ) : isComplete ? (
              <CheckCircleOutlined style={{ color: SHARED_STYLES.successColor, fontSize: 16 }} />
            ) : undefined,
          status:
            index < currentStep || isComplete
              ? "finish"
              : index === currentStep
                ? "process"
                : "wait",
        }))}
        style={{
          marginTop: 8,
        }}
      />
    </div>
  );
};

export default GenerationProgress;

